// What a tile is: kinds (links, webtop links, applet commands, groups; widgets live in widgets/), the tiles drawn on the webtop
// (onWebtop, all but grouped ones), a link's target as typed and shown, opening a tile, and the icon it shows.
import { WIDGETS } from '../widgets/index.js';
import { appletIcon, assetSrc, commandReady, commandTarget, commandText, runCommand } from './applets.js';
import { openGroup } from './groups.js';
import { byId, webtop } from './state.js';
import { goTo } from './nav.js';
import { toast } from './ui.js';
import { DEFAULT_ICON, forgetIcons, iconFor, isProxied, knownIcon } from './icons.js';
import { assetName, slugOfPath, webtopHref } from '/model.js';

// Every icon image comes from /icons/<key> (server/routes/icons.js); DEFAULT_ICON (client/lib/icons.js: /logo.svg, versioned) is
// webtop's own logo, drawn for a tile with nothing better and whenever an icon image fails to load.
// What older versions stored when no icon was given (🔗 for links, 🗂️ for webtop links); they mean "no icon".
export const PLACEHOLDER_ICONS = ['🔗', '🗂️'];

export const isImage = (icon) => /^(https?:)?\/\/|^\/|^data:image\//.test(icon || '');
// A text icon is a single glyph: the first grapheme (an emoji may be several code units).
const segmenter = typeof Intl.Segmenter === 'function' ? new Intl.Segmenter(undefined, { granularity: 'grapheme' }) : null;
export function firstGlyph(text) {
  const t = (text || '').trim();
  if (!t) return '';
  if (segmenter) { for (const seg of segmenter.segment(t)) return seg.segment; }
  return [...t][0];
}
// Groups: a group tile lists its members' ids in data.items; a tile in a group is not drawn on the webtop by itself
// (it shows in the group's panel instead). Links, webtop links, applet commands and other groups can be grouped, so groups nest like
// folders, as deep as they go: each tile is in one group at most and never inside itself (the server's
// store.tidyGroups keeps the same rules).
export const GROUPABLE = ['url', 'webtop', 'command', 'group'];
export const isGroup = (s) => s?.kind === 'group';
export const groupItems = (g) => (g.data?.items ?? []).map(byId).filter((s) => s && GROUPABLE.includes(s.kind));
export const parentGroup = (s) => webtop.shortcuts.find((g) => isGroup(g) && g.data?.items?.includes(s.id)) ?? null;
// The groups from the webtop down to g: [top-level group, ..., g].
export function groupPath(g) {
  const path = [];
  for (let p = g; p && !path.includes(p); p = parentGroup(p)) path.unshift(p);
  return path;
}
// Every tile inside g, however far down, in the order they read (a subgroup, then what is in it).
export function allMembers(g, seen = new Set([g])) {
  return groupItems(g).flatMap((m) => {
    if (seen.has(m)) return [];
    seen.add(m);
    return isGroup(m) ? [m, ...allMembers(m, seen)] : [m];
  });
}
export const groupLinks = (g) => allMembers(g).filter((m) => !isGroup(m));
// Whether s may go into group `into`: anything groupable, but a group not into itself or anything inside it.
export const canJoin = (into, s) => GROUPABLE.includes(s.kind) && s !== into && !(isGroup(s) && groupPath(into).includes(s));
function groupedIds() {
  const ids = new Set();
  for (const g of webtop.shortcuts) if (isGroup(g)) for (const s of groupItems(g)) ids.add(s.id);
  return ids;
}
// The tiles drawn on the webtop: all but the grouped ones.
export function onWebtop() {
  const grouped = groupedIds();
  return webtop.shortcuts.filter((s) => !grouped.has(s.id));
}
// A tile with nothing to open, or an applet command this browser cannot run (the applet missing or disabled).
export const needsAttention = (s) => !WIDGETS[s.kind] && !isGroup(s) && (!s.target || (s.kind === 'command' && !commandReady(s.target)));

// One field decides the kind: "/@work" or "/" is a webtop, "@hello" or "@hello/toast" an applet or one of its commands
// (client/lib/applets.js), anything else is a web link.
export function classifyTarget(raw) {
  let v = raw.trim();
  if (!v) return { kind: 'url', target: '' };
  if (v.startsWith('@')) {
    const { target, error } = commandTarget(v);
    return error ? { error } : { kind: 'command', target };
  }
  if (v.startsWith('/')) {
    const slugPart = slugOfPath(v.replace(/^\/+/, '/').toLowerCase());
    if (!slugPart) return { error: 'Webtop: /@ then letters, digits and dashes, e.g. /@work' };
    return { kind: 'webtop', target: slugPart };
  }
  if (!/^[a-z][a-z0-9+.-]*:/i.test(v)) v = `https://${v}`;
  try { new URL(v); } catch { return { error: 'Enter a web address or /@webtop' }; }
  return { kind: 'url', target: v };
}
export function targetText(s) {
  if (s.kind === 'webtop') return webtopHref(s.target);
  if (s.kind === 'command') return s.target ? commandText(s.target) : '';
  return s.target;
}

export function openShortcut(s) {
  if (WIDGETS[s.kind]) return; // widgets have nothing to open
  if (isGroup(s)) return openGroup(s);
  if (!s.target) return toast('This shortcut has no link yet. Right-click it and choose Edit.', { kind: 'info' });
  if (s.kind === 'webtop') goTo(targetText(s));
  else if (s.kind === 'command') runCommand(s.target).catch(toast); // offers to install or enable a missing applet
  else window.open(s.target, '_blank', 'noopener');
}

// Icons load only once they are on screen (or about to be). A link's first icon request can make the server fetch
// a favicon from the site, so a webtop of hundreds of imported bookmarks asking for all of them at once was slow,
// and most were below the screen, in closed groups or in a launcher nobody opened. An address that has loaded once
// is set at once from then on, so a redraw (which builds new elements) never blinks an icon that is already there.
const LAZY_MARGIN = '200px'; // start a little before an icon scrolls in, so it is there when it arrives
const loadedIcons = new Set();
let iconWatch = null;
const watchedIcons = new Set();
let iconSweep = 0;
function unwatch(img) {
  iconWatch.unobserve(img);
  watchedIcons.delete(img);
}
// Images a redraw dropped before they came on screen stop being watched (a frame after they were made, when every
// image that is drawn has been put on the page), so the observer holds nothing that is gone.
function sweepIcons() {
  iconSweep = 0;
  for (const img of watchedIcons) if (!img.isConnected) unwatch(img);
}
// A site's icon or an image given by address goes through this browser's icon cache (client/lib/icons.js): IndexedDB first,
// else webtop's icon proxy, and the image shows what it answers (the stored image, or the logo). Anything else (a
// brand, an image an older webtop saved, the logo) loads directly.
function showIcon(img, src) {
  if (!isProxied(src)) { img.src = src; return; }
  iconFor(src).then((url) => { img.src = url; });
}
// What an icon shows at once, with no waiting: one this page already has. Undefined otherwise.
const ready = (src) => knownIcon(src) ?? (loadedIcons.has(src) ? src : undefined);
function lazyIcon(img, src) {
  // an icon uploaded to the webtop: an asset, shown as applets/storage/src/assets.js says (a `_` webtop's is in this browser's storage)
  if (assetName(src)) { assetSrc(src).then((url) => { img.src = url; }); return; }
  if (!isProxied(src)) img.addEventListener('load', () => loadedIcons.add(src), { once: true });
  const now = ready(src);
  if (now) { img.src = now; return; }
  if (typeof IntersectionObserver !== 'function') { showIcon(img, src); return; }
  iconWatch ??= new IntersectionObserver((entries) => {
    for (const e of entries) if (e.isIntersecting) { unwatch(e.target); showIcon(e.target, e.target.dataset.src); }
  }, { rootMargin: LAZY_MARGIN });
  img.dataset.src = src;
  watchedIcons.add(img);
  iconWatch.observe(img); // an image not on the page yet is reported once it is put there
  iconSweep ||= requestAnimationFrame(sweepIcons);
}

// An icon that has not loaded on this page yet waits under the 'icon-wait' class, which (style.css) shows webtop's
// logo in its place only once it has taken longer than a moment, so a slow first fetch never leaves a blank tile
// while icons the browser already has (nearly all of them) paint before the placeholder could flash.
export function iconNode(icon, cls) {
  const el = document.createElement('div');
  el.className = cls;
  if (icon?.startsWith(APPLET_ICON)) {
    // an applet command's tile: the applet's own icon, found in this browser (or on the server) when drawn
    const img = document.createElement('img');
    img.alt = ''; img.draggable = false;
    el.classList.add('icon-wait');
    img.addEventListener('load', () => el.classList.remove('icon-wait'));
    img.onerror = () => { if (!img.src.endsWith(DEFAULT_ICON)) img.src = DEFAULT_ICON; else el.classList.remove('icon-wait'); };
    appletIcon(icon.slice(APPLET_ICON.length)).then((url) => { img.src = url || DEFAULT_ICON; }, () => { img.src = DEFAULT_ICON; });
    el.appendChild(img);
  } else if (isImage(icon)) {
    const img = document.createElement('img');
    img.alt = ''; img.draggable = false;
    if (!ready(icon)) el.classList.add('icon-wait');
    img.addEventListener('load', () => el.classList.remove('icon-wait'));
    img.onerror = () => {
      if (!img.src.endsWith(DEFAULT_ICON)) img.src = DEFAULT_ICON;
      else el.classList.remove('icon-wait');
    };
    lazyIcon(img, icon);
    el.appendChild(img);
  } else if (firstGlyph(icon)) {
    el.textContent = firstGlyph(icon);
  } else {
    return iconNode(DEFAULT_ICON, cls);
  }
  return el;
}

// Icons no tile shows any more leave this browser's icon cache (client/lib/icons.js): forgetTileIcons for tiles that went,
// forgetDroppedIcons with the tiles before and after a change, which drops the icon of every tile that is gone or now
// shows another one (a deletion, an edited address or icon, an import that replaces, the assistant). Resolve once
// IndexedDB has let them go.
export function forgetTileIcons(tiles) {
  return forgetIcons([...new Set(tiles.map(tileIcon).filter(isProxied))]);
}
export function forgetDroppedIcons(before, after) {
  const now = new Map((after ?? []).map((s) => [s.id, tileIcon(s)]));
  return forgetTileIcons((before ?? []).filter((s) => now.get(s.id) !== tileIcon(s)));
}

// What a tile shows as its icon: its own (an emoji, an image given by address, shown through the proxy as
// /icons/image/<address>, or an older /icons/<file>), or else, for a link, its site's icon by host, /icons/<host>
// (every link to a site shares it, in this browser's cache and in Cloudflare's); anything else (a webtop link, a link
// without an http(s) address yet) shows webtop's logo. An applet command shows its applet's icon (APPLET_ICON).
const APPLET_ICON = 'applet-icon:';
export function tileIcon(s) {
  if (/^https?:\/\//i.test(s.icon ?? '')) return `/icons/image/${encodeURIComponent(s.icon)}`;
  if (s.icon && !PLACEHOLDER_ICONS.includes(s.icon)) return s.icon;
  if (s.kind === 'command' && s.target) return `${APPLET_ICON}${s.target.split(/[/?]/)[0]}`;
  if (s.kind !== 'url' || !s.target) return DEFAULT_ICON;
  try {
    const u = new URL(s.target);
    // The host as the server names it: lower case, no trailing dot, and no port 80 or 443 (whichever scheme says
    // it), since the server tries https then http on the bare name; so each site has one key here and at Cloudflare.
    const host = u.hostname.replace(/\.$/, '') + (u.port && u.port !== '80' && u.port !== '443' ? `:${u.port}` : '');
    if (/^https?:$/.test(u.protocol) && u.hostname) return `/icons/${encodeURIComponent(host)}`;
  } catch {}
  return DEFAULT_ICON;
}
